<template>
  <soha-page-loading v-if="loading" />
  <div class="card card-custom gutter-b example example-compact p-8" v-else>
    <div class="row m-0">
      <span class="soha-page-title">
        {{
          editable
            ? $t(`${I18nPrefix}EDIT_PAGE_TITLE`)
            : $t(`${I18nPrefix}CREATE_PAGE_TITLE`)
        }}
      </span>
    </div>
    <div class="row mx-0 mt-10">
      <div class="col-sm-6 col-12">
        <soha-text-field
          name="testName"
          :placeholder="$t(`${I18nPrefix}NAME`)"
          class="w-100"
          v-model="name.value"
          :error="name.error"
        />
      </div>
      <div class="col-sm-6 col-12">
        <soha-text-field
          name="testTime"
          type="number"
          :placeholder="$t(`${I18nPrefix}TIME_PLACEHOLDER`)"
          class="w-100"
          v-model="time.value"
          :error="time.error"
        />
      </div>
      <div class="col-12">
        <soha-text-area
          name="testDescription"
          class="w-100"
          v-model="description"
          :placeholder="$t(`${I18nPrefix}DESCRIPTION`)"
        />
      </div>
    </div>

    <div class="row m-0">
      <div class="col-12 text-center" v-if="questions.length === 0">
        <soha-no-data :message="$t(`${I18nPrefix}EMPTY_QUESTION_LIST`)" />
      </div>
      <div v-else class="col-12">
        <h5 class="my-5">{{ $t(`${I18nPrefix}QUESTIONS_TEST`) }}</h5>
        <draggable
          class="row m-0"
          group="people"
          v-model="questions"
          :disabled="screenSize <= 700"
          :key="draggableKey"
        >
          <div
            class="col-12 my-4"
            v-for="(question, index) in questions"
            :key="index"
            :draggable="true"
          >
            <div class="soha-manage-test-card">
              <span class="soha-test-card-before">
                <inline-svg src="/media/svg/soha/icons/draggable.svg" />
              </span>
              <div class="soha-manage-test-card-header row m-0">
                <div
                  class="d-flex col-xl-10 col-lg-10 col-md-10 col-sm-9 col-12 p-0 soha-manage-name"
                >
                  <span class="pr-2 d-inline-block" style="font-size: 1.231rem">
                    {{ toPersianDigits(index + 1) + "- " }}
                  </span>
                  <span
                    class="soha-manage-name d-inline-block"
                    v-html="question.title"
                  />
                </div>

                <div
                  class="col-xl-2 col-lg-2 col-md-2 col-sm-3 col-12 p-0 d-flex justify-content-between justify-content-sm-end mt-5 mt-sm-0"
                >
                  <div class="mx-4">
                    <soha-button-icon
                      v-b-tooltip.hover="{ customClass: 'soha-tooltip-class' }"
                      :title="$t(`${I18nPrefix}GO_UP`)"
                      border-color="box-border"
                      icon="arrow-down"
                      @click="moveUp(index)"
                      style="transform: rotate(180deg)"
                      v-if="index != 0"
                      small
                      icon-size="sm"
                      icon-color="grey"
                    />
                  </div>
                  <div class="mr-4">
                    <soha-button-icon
                      v-b-tooltip.hover="{ customClass: 'soha-tooltip-class' }"
                      :title="$t(`${I18nPrefix}GO_DOWN`)"
                      icon="arrow-down"
                      border-color="box-border"
                      @click="moveDown(index)"
                      v-if="index != questions.length - 1"
                      small
                      icon-size="sm"
                      icon-color="grey"
                    />
                  </div>
                  <soha-menu>
                    <template v-slot:activator>
                      <span name="manageTestCardMenu" class="cursor-pointer">
                        <inline-svg src="/media/svg/soha/btn-icons/menu.svg" />
                      </span>
                    </template>
                    <soha-list>
                      <soha-list-item
                        class="primary-color"
                        :text="$t(`${I18nPrefix}EDIT`)"
                        icon="edit"
                        name="editQuestion"
                        @click="openEditQuestionModal(question, index)"
                      />
                      <soha-list-item
                        :text="$t(`${I18nPrefix}DELETE`)"
                        icon="delete"
                        @click="questions.splice(index, 1)"
                        name="removeQuestion"
                      />
                    </soha-list>
                  </soha-menu>
                </div>
              </div>
              <div class="row m-0" v-if="question.type == textQuestionId">
                <div class="soha-message-primary-text p-0">
                  {{ toPersianDigits(question.score) }}
                  {{ $t(`${I18nPrefix}SCORE`) }}
                </div>
              </div>
              <div
                class="row mx-0 mb-4 align-items-center"
                v-for="(item, itemIndex) in question.items"
                :key="itemIndex"
                v-else
              >
                <span class="font-size-sm pr-3">
                  {{ $t(`${I18nPrefix}ITEM`) }}
                  {{ toPersianDigits(itemIndex + 1) }}
                </span>
                <div class="soha-manage-test-item-title" v-html="item.title" />
                <span class="soha-manage-test-score-tag font-size-sm">
                  {{ toPersianDigits(item.score) }}
                  {{ $t(`${I18nPrefix}SCORE`) }}
                </span>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <div class="row m-0 px-8">
      <soha-add-new-item
        :text="$t(`${I18nPrefix}ADD_QUESTION`)"
        @addNewItem="$bvModal.show('manage-question')"
      />
    </div>

    <div class="row mx-0 mt-10 justify-content-end">
      <soha-button
        name="insertTest"
        @click="editable ? editTest() : insertTest()"
        :loading="btnLoading"
        :text="editable ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}CREATE`)"
      />
    </div>

    <soha-modal
      size="lg"
      id="manage-question"
      @hide="clearDataModal"
      :title="
        questionEdit
          ? $t(`${I18nPrefix}EDIT_QUESTION`)
          : $t(`${I18nPrefix}ADD_QUESTION`)
      "
    >
      <template v-slot:body>
        <div class="row m-0">
          <div class="col-12">
            <soha-select
              class="w-100"
              v-model="type.value"
              :error="type.error"
              name="questionType"
              :placeholder="$t(`${I18nPrefix}TYPE`)"
              :options="questionType"
            />
          </div>

          <div class="col-12">
            <div class="col-12 px-0">
              <label class="d-block">{{ $t(`${I18nPrefix}TITLE`) }}</label>
              <soha-text-editor
                name="questionName"
                v-model.trim="questionName"
                :style="
                  questionNameError
                    ? 'border: 1px solid red !important; border-radius: 5px;'
                    : ''
                "
              />

              <span class="font-size-sm text-red" v-if="questionNameError">
                {{ $t(`${I18nPrefix}QUESTION_NAME_ERROR`) }}
              </span>
            </div>
            <div class="col-12 px-0 mt-5" v-if="type.value === textQuestionId">
              <soha-text-field
                name="questionScore"
                type="number"
                :placeholder="$t(`${I18nPrefix}SCORE`)"
                class="w-100"
                v-model="scoreTextQuestion.value"
                :error="scoreTextQuestion.error"
              />
            </div>
          </div>
          <div
            class="col-12"
            v-if="type.value !== textQuestionId && type.value"
          >
            <div class="col-12 mx-0 mt-5">
              <label class="d-block">{{ $t(`${I18nPrefix}ITEMS`) }}</label>
            </div>
            <div
              v-for="(answerItem, i) in answerItems"
              :key="i"
              class="row m-0"
            >
              <div class="col-md-8 col-12">
                <soha-text-field
                  name="questionTitle"
                  :placeholder="$t(`${I18nPrefix}TITLE`)"
                  class="w-100"
                  v-model.trim="answerItem.title"
                  :error="answerItem.titleError"
                />
              </div>
              <div class="col-md-2 col-sm-6 col-12">
                <soha-text-field
                  name="questionScore"
                  :placeholder="$t(`${I18nPrefix}SCORE`)"
                  class="w-100"
                  v-model.trim="answerItem.score"
                  :error="answerItem.scoreError"
                />
              </div>
              <div
                class="col-md-2 col-sm-6 col-12"
                style="padding-top: 0.462rem"
                v-if="i !== 0"
              >
                <soha-button
                  class="w-100"
                  name="deleteItem"
                  variant="danger"
                  @click="removeItem(i)"
                  :text="$t(`${I18nPrefix}DELETE`)"
                />
              </div>
            </div>
            <div class="col-12 mt-5">
              <soha-add-new-item
                name="addItem"
                v-if="type.value !== textQuestionId && type.value"
                :text="$t(`${I18nPrefix}ADD_ITEM`)"
                @addNewItem="addItem"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <soha-button
          name="manageQuestion"
          @click="
            questionEdit
              ? editQuestion(questionIndex, type.value)
              : addQuestion()
          "
          :text="
            questionEdit ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}INSERT`)
          "
        />
      </template>
    </soha-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import ValidationMixin from "@/mixins/ValidationMixin";
import UIMixin from "@/mixins/UIMixin";
import { mapGetters } from "vuex";
import draggable from "vuedraggable";

export default {
  name: "CustomTestCreateEdit",

  mixins: [ValidationMixin, UIMixin],

  components: { draggable },

  data() {
    return {
      I18nPrefix: "PAGES.SETTING.CREATE_EDIT_CUSTOM_TEST.",
      loading: false,
      textQuestionId: "3",
      btnLoading: false,
      questionNameError: null,
      questionName: null,
      questionType: [
        {
          text: this.$t(`PAGES.SETTING.CREATE_EDIT_CUSTOM_TEST.CHECKBOX`),
          id: "1",
        },
        {
          text: this.$t(`PAGES.SETTING.CREATE_EDIT_CUSTOM_TEST.RADIO_BUTTON`),
          id: "2",
        },
        {
          text: this.$t(`PAGES.SETTING.CREATE_EDIT_CUSTOM_TEST.DESCRIPTIVE`),
          id: "3",
        },
      ],
      answerItems: [
        {
          title: null,
          score: null,
          titleError: null,
          scoreError: null,
        },
      ],
      name: {
        value: null,
        error: null,
      },
      time: {
        value: null,
        error: null,
      },
      type: {
        value: null,
        error: null,
      },
      description: null,
      scoreTextQuestion: {
        value: null,
        error: null,
      },
      questionEdit: false,
      questionIndex: null,
      questions: [],
      testId: null,
      screenSize: 0,
      draggableKey: 0,
      editable: false,
    };
  },

  mounted() {
    const { query, params } = this.$route;
    this.testId = query.id || params.id;
    this.editable = !!query.id;

    if (this.testId) {
      this.getTestInfo();
    }
  },

  methods: {
    clearDataModal() {
      this.type = {
        value: null,
        error: null,
      };
      this.scoreTextQuestion = {
        value: null,
        error: null,
      };
      this.answerItems = [
        {
          title: null,
          score: null,
          titleError: null,
          scoreError: null,
        },
      ];
      this.questionName = null;
      this.questionNameError = null;
      this.questionEdit = false;
      this.questionIndex = null;
    },

    removeItem(index) {
      this.answerItems.splice(index, 1);
    },

    addItem() {
      this.answerItems.push({
        title: null,
        score: null,
        titleError: null,
        scoreError: null,
      });
    },

    addQuestion() {
      if (this.validateQuestionItem()) {
        this.questions.push({
          type: this.type.value,
          title: this.questionName,
          score:
            this.type.value === this.textQuestionId
              ? this.scoreTextQuestion.value
              : "",
          items:
            this.type.value === this.textQuestionId ? [] : this.answerItems,
        });
        this.$bvModal.hide("manage-question");
      }
    },

    editQuestion(index, questionType) {
      if (this.validateQuestionItem()) {
        this.questions[index] = {
          items: questionType == this.textQuestionId ? [] : this.answerItems,
          score: this.scoreTextQuestion.value,
          title: this.questionName,
          type: this.type.value,
        };
        this.$bvModal.hide("manage-question");
      }
    },

    validateQuestionItem() {
      let error = false;
      this.type.error = this.validateField(
        this.type.value,
        this.$t(`${this.I18nPrefix}TYPE`)
      );
      this.questionNameError =
        this.questionName ===
        `<p class="ql-direction-rtl ql-align-right"><br></p>`;
      if (this.type.value === this.textQuestionId) {
        this.scoreTextQuestion.error = this.validateField(
          this.scoreTextQuestion.value,
          this.$t(`${this.I18nPrefix}SCORE`)
        );
      } else if (this.type.value) {
        this.scoreTextQuestion = {
          value: null,
          error: null,
        };
        this.answerItems.map((answer) => {
          answer.titleError = this.validateField(
            answer.title,
            this.$t(`${this.I18nPrefix}TITLE`)
          );
          answer.scoreError = answer.score
            ? null
            : this.$t(`${this.I18nPrefix}SCORE`);
          if (answer.titleError || answer.scoreError) error = true;
        });
      }
      return !(
        this.type.error ||
        this.questionNameError ||
        this.scoreTextQuestion.error ||
        error
      );
    },

    openEditQuestionModal(question, index) {
      this.type.value = question.type;
      this.answerItems = question.items.map((item) => {
        return {
          error: null,
          score: item.score,
          title: item.title,
        };
      });
      this.questionName = question.title;
      this.scoreTextQuestion.value = question.score;
      this.questionEdit = true;
      this.questionIndex = index;
      this.$bvModal.show("manage-question");
    },

    convertTimeToHours(time) {
      return Math.floor(time / 60) + ":" + (time % 60) + ":00";
    },

    insertTest() {
      this.name.error = this.validateField(
        this.name.value,
        this.$t(`${this.I18nPrefix}NAME`)
      );
      this.time.error = this.validateField(
        this.time.value,
        this.$t(`${this.I18nPrefix}TIME`)
      );
      if (this.name.error || this.time.error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      } else if (this.questions.length === 0) {
        this.errorToast(this.$t(`${this.I18nPrefix}EMPTY_QUESTION_LIST`));
        return;
      }
      this.btnLoading = true;
      ApiService.post("api/employer/customTests/store", {
        companyId: this.companyId,
        title: this.name.value,
        description: this.description,
        time: this.convertTimeToHours(this.time.value),
        questions: this.questions,
      }).then((data) => {
        if (+data.data.code === 201) {
          this.successToast(data.data.message);
          this.$router.push({ name: "custom-test-setting" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    editTest() {
      this.name.error = this.validateField(
        this.name.value,
        this.$t(`${this.I18nPrefix}NAME`)
      );
      this.time.error = this.validateField(
        this.time.value,
        this.$t(`${this.I18nPrefix}TIME`)
      );
      if (this.name.error || this.time.error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      } else if (this.questions.length === 0) {
        this.errorToast(this.$t(`${this.I18nPrefix}EMPTY_QUESTION_LIST`));
        return;
      }
      this.btnLoading = true;
      ApiService.post("api/employer/customTests/update", {
        companyId: this.companyId,
        customTestId: this.testId,
        title: this.name.value,
        description: this.description,
        time: this.convertTimeToHours(this.time.value),
        questions: this.questions,
      }).then((data) => {
        if (+data.data.code === 204) {
          this.successToast(data.data.message);
          this.$router.push({ name: "custom-test-setting" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    getTestInfo() {
      this.loading = true;
      ApiService.post("api/employer/customTests/show", {
        customTestId: this.testId,
        resumeId: null,
      }).then((data) => {
        if (!data.data.code) {
          this.questions = data.data.questions;
          this.name.value = this.editable ? data.data.title : null;
          this.time.value = this.convertTimeToMinutes(data.data.time);
          this.description = data.data.description;
        } else {
          this.errorToast(data.data.message);
        }
        this.loading = false;
      });
    },

    convertTimeToMinutes(time) {
      let splitTime = time.split(":");
      return splitTime[0] * 60 + +splitTime[1];
    },

    handleResize() {
      this.screenSize = window.innerWidth;
    },

    moveDown(index) {
      let first = this.questions[index];
      this.questions[index] = this.questions[index + 1];
      this.questions[index + 1] = first;
      this.draggableKey++;
    },

    moveUp(index) {
      let first = this.questions[index];
      this.questions[index] = this.questions[index - 1];
      this.questions[index - 1] = first;
      this.draggableKey++;
    },
  },

  computed: {
    ...mapGetters(["companyId", "canDo"]),
  },

  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },

  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.soha-manage-test-card {
  content: "";
  border: 0.077rem solid var(--box-border-color);
  border-radius: 0.462rem;
  padding: 0.769rem 2.692rem 0.769rem 0.769rem;
  height: 100%;
  border-top-right-radius: 0.308rem;
  border-bottom-right-radius: 0.308rem;
  position: relative;
}
.soha-test-card-before {
  width: 2.308rem;
  height: 100%;
  position: absolute;
  right: 0;
  top: 0;
  border-top-right-radius: 0.308rem;
  border-bottom-right-radius: 0.308rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.soha-test-card-before:hover {
  cursor: all-scroll;
}
.soha-manage-test-card-header {
  display: flex;
  border-bottom: 0.077rem solid var(--box-border-color);
  padding-bottom: 0.769rem;
  margin-bottom: 0.769rem;
  color: var(--primary-color);
}
.soha-manage-name {
  color: var(--primary-color);
  font-weight: bold;
  text-align: right;
}
.soha-message-primary-text {
  color: var(--primary-color);
}
.soha-manage-test-score-tag {
  background: var(--light-secondary-color);
  color: var(--secondary-color);
  padding: 0.154rem 0.769rem;
  border: 0.077rem solid var(--secondary-color);
  border-radius: 0.385rem;
}
.soha-manage-test-item-title {
  color: var(--primary-color);
  margin: 0 0.769rem;
}
</style>
<style>
.soha-manage-name > * {
  margin: 0 !important;
}
.soha-manage-name img {
  max-width: 38.462rem !important;
}

@media (max-width: 768px) {
  .soha-manage-name img {
    max-width: 19.231rem !important;
  }
}
@media (max-width: 530px) {
  .soha-manage-name img {
    max-width: 15.385rem !important;
  }
}
@media (max-width: 400px) {
  .soha-manage-name img {
    max-width: 7.692rem !important;
  }
}
</style>
